import * as React from "react";

import { useAuth } from "@/providers";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";
import { useView } from "@/shared/domain/views/hooks";
import {
  RobotoNotFoundException,
  RobotoUnauthorizedException,
} from "@/shared/http";

import { useDefaultView } from "./useDefaultView";

export interface UseDefaultViewArrivalOptions {
  /** Resource type whose default to apply. */
  target: QueryTarget;
  /**
   * Whether the URL named a View when this mounted. The default stands down if
   * it did — a shared link is an explicit request, and beats a standing
   * preference.
   *
   * Read once, at mount, by the caller. Reading it live here would be wrong in
   * both directions: an applied default writes `?view=` itself, so a live read
   * would see a link that this hook had just created.
   */
  deepLinkPresent: boolean;
  /**
   * Apply the View. Same handler the deep link uses, so a default arrives
   * through exactly the path a chosen View does.
   */
  onResolved: (view: ViewRecord) => Promise<boolean>;
}

/**
 * The id will never resolve for this customer: the View was deleted, belongs to
 * another org, or has stopped being shared with them.
 *
 * Anything else — a 500, a dropped connection, a timeout — might resolve on the
 * next visit, and must not be treated as a reason to throw the default away.
 */
function isPermanentlyUnreadable(error: unknown): boolean {
  return (
    error instanceof RobotoNotFoundException ||
    error instanceof RobotoUnauthorizedException
  );
}

/**
 * Open a table on the View this customer chose to start from.
 *
 * Position 3 of the arrival order (ENG-3021): a `?view=` link, then what you
 * were doing (ENG-3035, not built), then this, then your org's default
 * (ENG-2890, not built), then everything.
 *
 * Precedence is expressed as a condition rather than a scheduler because there
 * are two sources today and one of them can be settled synchronously. Both
 * resolve asynchronously, and `useApplyView`'s sequence counter makes
 * concurrent applies *safe* without making them *ordered* — so letting both
 * start would hand the arrival to whichever fetch happened to finish last.
 * When ENG-3035 adds a third source this is the thing to fold into a resolver
 * proper; a growing conjunction of "unless someone else claimed it" flags is
 * the shape to avoid.
 */
export function useDefaultViewArrival({
  target,
  deepLinkPresent,
  onResolved,
}: UseDefaultViewArrivalOptions): void {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id ?? null;
  const { defaultViewId, clearDefault } = useDefaultView(target);

  // Orgs whose arrival this mount has already settled.
  //
  // A latch is needed at all because without one the resolved record re-applies
  // on every render, and the clear below — a write on a read path — re-fires
  // against itself.
  //
  // A set rather than a single "the org I last handled" slot. That slot needed a
  // rule per case — advance it on a successful apply, on an error, and again for
  // an org that had no default to fetch — and each rule was added after the
  // previous shape got something wrong. Membership needs none of them: an org is
  // either decided or it is not, and visiting another org cannot make an
  // existing answer wrong.
  //
  // Settled stays settled for the life of the mount, including across a return
  // to an org already handled. Nothing clears the table on an org switch —
  // `loadedView` and the filter state have no org awareness — so that View is
  // still applied, and re-applying it would discard whatever the customer has
  // done in the table since.
  //
  // Per mount rather than persisted: the question is "have I already decided
  // what this table opens with, this time round", which a fresh mount should
  // ask again.
  const settledOrgsRef = React.useRef<Set<string>>(new Set());
  const settleFor = React.useCallback((org: string | null) => {
    if (org !== null) {
      settledOrgsRef.current.add(org);
    }
  }, []);
  const alreadySettled = orgId !== null && settledOrgsRef.current.has(orgId);

  const pending =
    !deepLinkPresent && !alreadySettled && defaultViewId !== null
      ? defaultViewId
      : undefined;

  const { data, error } = useView(pending);

  React.useEffect(() => {
    if (pending !== undefined && data !== undefined) {
      settleFor(orgId);
      // Nothing here needs the outcome — unlike the deep link there is no URL
      // parameter being held open — but a rejection still has to go somewhere
      // rather than surfacing as an unhandled one. A stand-down means the
      // customer moved the table first, which is the right thing to lose to.
      void onResolved(data).catch(() => undefined);
    }
  }, [data, onResolved, orgId, pending, settleFor]);

  React.useEffect(() => {
    if (pending === undefined || error === null) {
      return;
    }

    // Settle on any failure: one attempt per org per mount, so a server that is
    // down does not turn into a retry loop.
    settleFor(orgId);

    if (!isPermanentlyUnreadable(error)) {
      // Transient. The table opens unfiltered this time, and the default is
      // still there to try again on the next visit.
      return;
    }

    // Gone for good, so stop every future arrival paying for the same failed
    // fetch. Best-effort: if the clear itself fails the default simply stays,
    // which is the state we were already in.
    void clearDefault().catch(() => undefined);
  }, [clearDefault, error, orgId, pending, settleFor]);
}
